import {
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  CommonQuestionKeys,
  Courses,
  ExpressionType,
  FieldType,
  PaymentMethod,
  TextInputType,
} from '@bybeam/platform-types';
import { AgreementOptions, YesNoOptions } from '@platform-ui/application/utils/index.js';
import { SpecialQuestionKeys } from '@platform-ui/utilities/application/configs/standards/types.js';
import { eq, gte, literal, or, sum } from '../../ast/utils.js';
import {
  COMMON_APPLICATION_PORTAL_STATUSES,
  COMMON_PAGES,
  COMMON_QUESTIONS,
  COMMON_SECTIONS as SHARED_SECTIONS,
} from './common.js';

const courses = Courses?.map(({ course, active }) => ({
  label: course,
  value: course,
  disabled: !active,
}));

// ----------- Career Builder track ------------- //
export const QUESTIONS: { [key: string]: ApplicationQuestion } = {
  firstTimeApplying: {
    key: 'firstTimeApplying',
    copy: { title: 'Is this your first time applying for incentives for a Career Builder course?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'firstTimeApplying',
        options: YesNoOptions,
      },
    ],
  },
  incentiveLevels: {
    key: 'incentiveLevels',
    copy: {
      title: 'Which incentive level are you applying for?',
      intro: `
Remember that you must submit a Level 1 claim before you can apply for Level 2 and 
you must submit a Level 2 claim before you can submit a Level 3 claim.

Not sure which level to choose? We can help. Call us at (855) 916-4091 and we will assist you.`,
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'incentiveLevel',
        options: [
          { value: 'level1', label: 'I am applying for Level 1 Training' },
          { value: 'secondLevel1', label: 'I am applying for Level 1 Training a 2nd time' },
          {
            value: 'level2',
            label: 'I am applying for Level 2 Practice (I submitted Level 1 at least one time)',
          },
          {
            value: 'level3',
            label: 'I am applying for Level 3 Pro (I submitted Level 1 and 2 already)',
          },
        ],
      },
    ],
  },
  //QUESTIONS GROUP LEVEL 1
  courseCompletedL1: {
    key: 'courseCompletedL1',
    copy: {
      title: 'Please select the Career Builder courses you completed.',
      intro:
        'Add proof of course completion for each course you completed. You must have completed 15 hours to be approved.\n\n  **_Note: Each course can be claimed only once. Do not submit the same course in multiple incentive claims._**',
    },
    fields: [
      {
        type: FieldType.Complex,
        key: 'courseCompletedL1.courses',
        copy: 'Course',
        subFields: [
          {
            type: FieldType.Dropdown,
            copy: 'Course',
            key: 'name',
            options: courses,
            props: { searchable: true },
          },
          {
            type: FieldType.Calculated,
            key: 'creditHours',
            display: { copy: 'Credit Hours' },
            formula: {
              type: ExpressionType.Condition,
              conditions: Courses.map(({ course, duration }) => ({
                if: eq('name', course),
                then: literal(duration),
              })),
              else: literal(0),
            },
          },
          {
            type: FieldType.Document,
            key: 'courseProof',
          },
        ],
      },
      {
        type: FieldType.Calculated,
        key: 'courseCompletedL1.totalCreditHours',
        display: {
          copy: 'Total Credit Hours',
          validation: {
            required: true,
            rules: [
              {
                rule: gte('courseCompletedL1.totalCreditHours', 15),
                message:
                  'You will need to complete at least 15 hours of courses before you’re able to apply for incentives',
              },
            ],
          },
        },
        formula: sum('courseCompletedL1.courses', 'creditHours'),
      },
    ],
  },
  // QUESTIONS GROUP SECOND LEVEL 1
  courseCompletedSL1: {
    key: 'courseCompletedSL1',
    copy: {
      title: 'Please select the course that you completed.',
      intro:
        'Add proof of course completion for each course you completed. You must have completed 15 hours to be approved.\n\n  **_Note: Each course can be claimed only once. Do not submit the same course in multiple incentive claims._**',
    },
    fields: [
      {
        type: FieldType.Complex,
        key: 'courseCompletedSL1.courses',
        copy: 'Course',
        subFields: [
          {
            type: FieldType.Dropdown,
            copy: 'Course',
            key: 'name',
            options: courses,
            props: { searchable: true },
          },
          {
            type: FieldType.Calculated,
            display: { copy: 'Credit Hours' },
            key: 'creditHours',
            formula: {
              type: ExpressionType.Condition,
              conditions: Courses.map(({ course, duration }) => ({
                if: eq('name', course),
                then: literal(duration),
              })),
              else: literal(0),
            },
          },
          {
            type: FieldType.Document,
            key: 'courseProof',
          },
        ],
      },
      {
        type: FieldType.Calculated,
        key: 'courseCompletedSL1.totalCreditHours',
        display: {
          copy: 'Total Credit Hours',
          validation: {
            required: true,
            rules: [
              {
                rule: gte('courseCompletedSL1.totalCreditHours', 15),
                message:
                  'You will need to complete at least 15 hours of courses before you’re able to apply for incentives',
              },
            ],
          },
        },
        formula: sum('courseCompletedSL1.courses', 'creditHours'),
      },
    ],
  },
  // QUESTIONS GROUP LEVEL 2
  workingDirectCareWorkL2: {
    key: 'workingDirectCareWorkL2',
    copy: {
      title:
        'Have you worked 40 hours as a Direct Care Worker in a 30 day period since you started your first Career Builder course?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'workingDirectCareWorkL2',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('workingDirectCareWorkL2', 'yes'),
              message: 'You will need to complete 40 hrs of work to continue your application.',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  numberEmployerL2: {
    key: 'numberEmployerL2',
    copy: { title: 'Do you have more than one employer?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'numberEmployerL2',
        options: YesNoOptions,
      },
    ],
  },
  additionalEmployerL2: {
    key: 'additionalEmployerL2',
    copy: { title: 'What is the name of the additional employer(s)? ' },
    dynamicLogic: eq('numberEmployerL2', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: 'additionalEmployerL2',
        copy: 'Name',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'name',
            copy: 'Name',
          },
        ],
      },
    ],
  },
  // QUESTIONS GROUP LEVEL 3
  workingDirectCareWorkL3: {
    key: 'workingDirectCareWorkL3',
    copy: {
      title:
        'Have you worked at least 40 hours per month as a Direct Care Worker for an additional 5 months since you claimed a Career Builder Level 2 incentive?',
    },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'workingDirectCareWorkL3',
        validation: {
          required: true,
          rules: [
            {
              rule: eq('workingDirectCareWorkL3', 'yes'),
              message:
                'You will need to complete 40 hrs of work for six months to proceed with your application.',
            },
          ],
        },
        options: YesNoOptions,
      },
    ],
  },
  numberEmployerL3: {
    key: 'numberEmployerL3',
    copy: { title: 'Do you have more than one employer?' },
    fields: [
      {
        type: FieldType.RadioList,
        key: 'numberEmployerL3',
        options: YesNoOptions,
      },
    ],
  },
  additionalEmployerL3: {
    key: 'additionalEmployerL3',
    copy: { title: 'What is the name of the additional employer(s)? ' },
    dynamicLogic: eq('numberEmployerL3', 'yes'),
    fields: [
      {
        type: FieldType.Complex,
        key: 'additionalEmployerL3',
        copy: 'Name',
        useLabelPrefix: false,
        subFields: [
          {
            type: FieldType.Text,
            inputType: TextInputType.Text,
            key: 'name',
            copy: 'Name',
          },
        ],
      },
    ],
  },
  surveyL1: {
    key: 'surveyL1',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL1.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL1.knowAboutCareerPossibilities',
        copy: 'I know more than before about career possibilities in the field of caregiving.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.wasAbleTotakeTrainings',
        copy: 'I was able to take trainings on topics that I am interested in and that are important for my work as a caregiver.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.learnedSkillsThatWillImproveMyWork',
        copy: 'I learned skills that will improve my work as a caregiver.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.learnedSkillsThatFeltMoreConfident',
        copy: 'I learned skills that will help me to feel more confident about the care I provide.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.ableToContinueWorkAsCareGiverInFuture',
        copy: 'I feel more able to continue my work as a caregiver in the future.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL1.ableToContinueWorkInMyCurrentJob',
        copy: 'I feel more able to continue to work in my current job(s).',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL1.willingToAnswer', 'yes'),
      },
    ],
  },
  surveyL2: {
    key: 'surveyL2',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL2.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL2.provideBetterCareUsingSkills',
        copy: 'I feel I provide better care using the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.moreConfident',
        copy: 'I feel more confident providing care because of the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.ableToContinueWorkAsCareGiverInFuture',
        copy: 'I feel more able to continue my work as a caregiver in the future.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL2.ableToContinueWorkInMyCurrentJob',
        copy: 'I feel more able to continue to work in my current job(s).',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL2.willingToAnswer', 'yes'),
      },
    ],
  },
  surveyL3: {
    key: 'surveyL3',
    copy: {
      title: `We value your feedback. Can you do a quick survey for us? It's an optional step that will keep your application outcome the same. Everything you say stays private.`,
    },
    skippable: true,
    fields: [
      {
        key: 'surveyL3.willingToAnswer',
        type: FieldType.RadioList,
        options: YesNoOptions,
        validation: { required: false },
      },
      {
        key: 'surveyL3.provideBetterCareUsingSkills',
        copy: 'I feel I provide better care using the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.moreConfident',
        copy: 'I feel more confident providing care because of the skills I learned through CalGrows.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.continueWorkingAsCaregiverNext12Months',
        copy: 'I plan to continue working as a caregiver for at least the next 12 months.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
      {
        key: 'surveyL3.continueWorkingInCurrentJobNext12Months',
        copy: 'I plan to continue to work in my current job for at least the next 12 months.',
        type: FieldType.RadioList,
        options: AgreementOptions,
        dynamicLogic: eq('surveyL3.willingToAnswer', 'yes'),
      },
    ],
  },
};

export const COMMON_SECTIONS: ApplicationSection[] = [
  SHARED_SECTIONS.aboutYou,
  SHARED_SECTIONS.personalInfo,
  // Incentive Level selection
  {
    key: 'incentiveLevelSelection',
    name: 'Incentive Level Selection',
    overview: {
      description: `
You will complete a full application for each incentive payment request. Once you complete
each application, you will be eligible to complete and submit another incentive payment
request.`,
      title: 'Incentive Level Selection',
    },
    questionGroups: [
      {
        key: 'firstTimeApplying',
        name: 'First Time Applying',
        questions: [QUESTIONS.firstTimeApplying],
      },
      {
        key: 'incentiveLevelsGroup',
        name: 'Incentive Level Selection',
        dynamicLogic: eq('firstTimeApplying', 'no'),
        questions: [QUESTIONS.incentiveLevels],
      },
      {
        key: 'level1',
        name: "You selected 'Level 1'",
        overview: {
          title: "You selected 'Level 1'",
          description: 'We have some additional questions to help us learn more',
        },
        dynamicLogic: or([eq('incentiveLevel', 'level1'), eq('firstTimeApplying', 'yes')]),
        questions: [QUESTIONS.courseCompletedL1],
      },
      {
        key: 'secondLevel1',
        name: "You selected 'Second Level 1'",
        overview: {
          title: "You selected ‘Second Level 1'",
          description:
            'Have you already submitted a Level 1 claim? If not, you must first submit a Level 1 claim.',
        },
        dynamicLogic: eq('incentiveLevel', 'secondLevel1'),
        questions: [QUESTIONS.courseCompletedSL1],
      },
      {
        key: 'level2',
        name: "You selected 'Level 2'",
        overview: {
          title: "You selected 'Level 2'",
          description:
            'Have you submitted at least one Level 1 claim? If not, you must first submit a Level 1 claim.',
        },
        dynamicLogic: eq('incentiveLevel', 'level2'),
        questions: [
          QUESTIONS.workingDirectCareWorkL2,
          QUESTIONS.numberEmployerL2,
          QUESTIONS.additionalEmployerL2,
        ],
      },
      {
        key: 'level3',
        name: "You selected 'Level 3'",
        overview: {
          title: "You selected 'Level 3'",
          description:
            'Have you submitted Level 1 and Level 2 claims? If not, you must first submit Level 1 and Level 2 claims before submitting a Level 3 claim.',
        },
        dynamicLogic: eq('incentiveLevel', 'level3'),
        questions: [
          QUESTIONS.workingDirectCareWorkL3,
          QUESTIONS.numberEmployerL3,
          QUESTIONS.additionalEmployerL3,
          COMMON_QUESTIONS.paystub,
        ],
      },
      {
        key: 'surveyLevel1',
        name: 'Survey for Incentive Level 1',
        questions: [QUESTIONS.surveyL1],
        dynamicLogic: or([
          eq('incentiveLevel', 'level1'),
          eq('firstTimeApplying', 'yes'),
          eq('incentiveLevel', 'secondLevel1'),
        ]),
      },
      {
        key: 'surveyLevel2',
        name: 'Survey for Incentive Level 2',
        questions: [QUESTIONS.surveyL2],
        dynamicLogic: eq('incentiveLevel', 'level2'),
      },
      {
        key: 'surveyLevel3',
        name: 'Survey for Incentive Level 3',
        questions: [QUESTIONS.surveyL3],
        dynamicLogic: eq('incentiveLevel', 'level3'),
      },
    ],
  },
  // Preferred payment method selection
  {
    key: 'preferredPaymentMethod',
    name: 'Preferred Payment Method',
    overview: {
      title: 'Preferred Payment Method',
      description: 'In this section we will ask for your preferred method of payment.',
    },
    questionGroups: [
      {
        key: 'preferredPaymentMethod',
        name: 'Preferred Payment Method',
        questions: [
          {
            key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
            copy: { title: 'How would you like to receive your payment?' },
            fields: [
              {
                key: CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
                type: FieldType.RadioList,
                options: [
                  {
                    label: 'Direct Deposit',
                    description:
                      'We recommend using this option if you have access to a bank account. You should receive your funds in 1-3 business days upon application approval.',
                    value: PaymentMethod.DirectDeposit,
                  },
                  {
                    label: 'Paper Check',
                    description:
                      'Receive a check in the mail in 7-10 business days upon application approval.',
                    value: PaymentMethod.Check,
                  },
                ],
              },
            ],
          },
          {
            key: SpecialQuestionKeys.BankAccount,
            copy: { title: 'BANK ACCOUNT INFORMATION' },
            fields: [],
            dynamicLogic: eq(
              CommonQuestionKeys.PREFERRED_PAYMENT_METHOD,
              PaymentMethod.DirectDeposit,
            ),
          },
        ],
      },
    ],
  },
  SHARED_SECTIONS.review,
];

const CONFIG: ApplicationConfig = {
  introPages: [COMMON_PAGES.welcome, COMMON_PAGES.whatToExpect, COMMON_PAGES.applicationProcess],
  overrides: {
    statuses: COMMON_APPLICATION_PORTAL_STATUSES,
  },
  sections: COMMON_SECTIONS,
};
export default CONFIG;
